import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import type { PortfolioReport } from '../lib/api';
import { fmtFte, fmtMoney, fmtDelta } from '../lib/format';
import { toast } from '../components/Toast';

export default function EnterpriseReporting() {
  const [report, setReport] = useState<PortfolioReport | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.portfolio();
      setReport(r);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // The API normalizes every model's dollars into one currency before summing.
  const reportCurrency = report?.currency || 'USD';
  const currencyWarnings = report?.currencyWarnings || [];

  const exportCsv = () => {
    if (!report) return;
    const headers = [
      'Client',
      'Industry',
      'Region',
      'Modeled FTE',
      'Current FTE',
      'Δ',
      'Y1 FTE',
      'Y3 FTE',
      'Y3 Released',
      `Y1 (${reportCurrency})`,
      `Y3 (${reportCurrency})`,
    ];
    const rows = report.clients.map((c) => [
      c.clientName,
      c.industry || '',
      c.region || '',
      c.modeledFte.toFixed(1),
      c.currentFte.toFixed(1),
      (c.modeledFte - c.currentFte).toFixed(1),
      c.y1Fte.toFixed(1),
      c.y3Fte.toFixed(1),
      c.y3Released.toFixed(1),
      c.y1Dollar.toFixed(0),
      c.y3Dollar.toFixed(0),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const compareData = useMemo(() => {
    if (!report) return [];
    return report.clients.slice(0, 12).map((c) => ({
      name: c.clientName.length > 18 ? c.clientName.slice(0, 18) + '…' : c.clientName,
      Modeled: c.modeledFte,
      Current: c.currentFte,
    }));
  }, [report]);

  const disciplineData = useMemo(() => {
    if (!report) return [];
    return report.byDiscipline.map((d) => ({
      name: d.label,
      Current: d.current,
      Modeled: d.modeled,
      Y3: d.y3,
    }));
  }, [report]);

  return (
    <div className="px-10 py-8 w-full">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Enterprise Reporting</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Portfolio rollup across every saved client. Use this to spot the largest opportunities.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary">
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={!report}
            className="btn-primary"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card card-body">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      ) : !report ? (
        <div className="card card-body text-slate-500">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <PortfolioKpi label="Clients" value={String(report.totals.clients)} />
            <PortfolioKpi label="Modeled FTE" value={fmtFte(report.totals.modeledFte, 0)} />
            <PortfolioKpi
              label="Δ vs current"
              value={fmtDelta(report.totals.delta, 0)}
              tone={report.totals.delta > 0 ? 'green' : 'red'}
            />
            <PortfolioKpi
              label={`Y3 opportunity (${reportCurrency})`}
              value={fmtMoney(report.totals.y3Dollar, reportCurrency)}
              accent
            />
          </div>

          {currencyWarnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-5">
              <div className="flex items-start gap-2">
                <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-amber-800">
                    {currencyWarnings.length} scenario
                    {currencyWarnings.length === 1 ? '' : 's'} excluded from the{' '}
                    {reportCurrency} totals
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    These models price in a currency with no {reportCurrency} conversion rate. Add
                    one on the model's Currency tab to include them. Their FTE still counts.
                  </p>
                  <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                    {currencyWarnings.slice(0, 6).map((w, i) => (
                      <li key={i}>
                        {w.clientName} · {w.scenarioName} ({w.currency})
                      </li>
                    ))}
                    {currencyWarnings.length > 6 && (
                      <li className="text-amber-600">
                        and {currencyWarnings.length - 6} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <section className="card">
              <div className="card-header">
                <h2 className="text-base font-semibold">Modeled vs current — top clients</h2>
              </div>
              <div className="card-body h-72">
                <ResponsiveContainer>
                  <BarChart data={compareData}>
                    <CartesianGrid stroke="#f1f5f9" />
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} angle={-20} textAnchor="end" height={70} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip
                      contentStyle={{
                        background: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Current" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Modeled" fill="#7B5BB6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2 className="text-base font-semibold">By discipline</h2>
              </div>
              <div className="card-body h-72">
                <ResponsiveContainer>
                  <BarChart data={disciplineData} layout="vertical">
                    <CartesianGrid stroke="#f1f5f9" />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                    <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={140} />
                    <Tooltip
                      contentStyle={{
                        background: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Current" fill="#cbd5e1" />
                    <Bar dataKey="Modeled" fill="#7B5BB6" />
                    <Bar dataKey="Y3" fill="#2E75B6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <SegmentTable
              title="By industry"
              rows={report.byIndustry}
              keyName="key"
              currency={reportCurrency}
            />
            <SegmentTable
              title="By region"
              rows={report.byRegion}
              keyName="key"
              currency={reportCurrency}
            />
          </div>

          <section className="card">
            <div className="card-header">
              <h2 className="text-base font-semibold">
                Top opportunities (Y3 {reportCurrency})
              </h2>
            </div>
            <div className="card-body overflow-x-auto">
              <table className="w-full text-sm tabular-nums">
                <thead>
                  <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
                    <th className="text-left py-2 pr-3">Client</th>
                    <th className="text-left py-2 px-2">Industry</th>
                    <th className="text-left py-2 px-2">Region</th>
                    <th className="text-right py-2 px-2">Modeled</th>
                    <th className="text-right py-2 px-2">Current</th>
                    <th className="text-right py-2 px-2">Y3 release</th>
                    <th className="text-right py-2 pl-2">Y3 ({reportCurrency})</th>
                  </tr>
                </thead>
                <tbody>
                  {report.clients.slice(0, 10).map((c) => (
                    <tr key={c.clientId} className="border-t border-slate-100">
                      <td className="py-2 pr-3">
                        <Link
                          to={`/clients/${c.clientId}`}
                          className="text-brand-purple-700 hover:underline"
                        >
                          {c.clientName}
                        </Link>
                      </td>
                      <td className="py-2 px-2 text-slate-600">{c.industry || '—'}</td>
                      <td className="py-2 px-2 text-slate-600">{c.region || '—'}</td>
                      <td className="py-2 px-2 text-right text-slate-700">{fmtFte(c.modeledFte)}</td>
                      <td className="py-2 px-2 text-right text-slate-500">{fmtFte(c.currentFte)}</td>
                      <td className="py-2 px-2 text-right text-slate-700">{fmtFte(c.y3Released)}</td>
                      <td className="py-2 pl-2 text-right text-brand-purple-700 font-medium">
                        {fmtMoney(c.y3Dollar, reportCurrency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function PortfolioKpi({
  label,
  value,
  tone,
  accent,
}: {
  label: string;
  value: string;
  tone?: 'green' | 'red';
  accent?: boolean;
}) {
  return (
    <div className="card p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
      <div
        className={
          accent
            ? 'text-2xl font-semibold mt-2 text-brand-purple-700 tabular-nums'
            : tone === 'green'
              ? 'text-2xl font-semibold mt-2 text-emerald-700 tabular-nums'
              : tone === 'red'
                ? 'text-2xl font-semibold mt-2 text-rose-700 tabular-nums'
                : 'text-2xl font-semibold mt-2 text-slate-900 tabular-nums'
        }
      >
        {value}
      </div>
    </div>
  );
}

function SegmentTable<
  T extends { key?: string; label?: string; modeled: number; y3Dollar?: number },
>({
  title,
  rows,
  keyName,
  currency,
}: {
  title: string;
  rows: T[];
  keyName: 'key' | 'label';
  currency: string;
}) {
  return (
    <section className="card">
      <div className="card-header">
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <div className="card-body overflow-x-auto">
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
              <th className="text-left py-2 pr-3">Segment</th>
              <th className="text-right py-2 px-2">Modeled FTE</th>
              <th className="text-right py-2 pl-2">Y3 ({currency})</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r[keyName])} className="border-t border-slate-100">
                <td className="py-2 pr-3 text-slate-700">{String(r[keyName])}</td>
                <td className="py-2 px-2 text-right">{fmtFte(r.modeled, 0)}</td>
                <td className="py-2 pl-2 text-right text-brand-purple-700">
                  {fmtMoney(r.y3Dollar || 0, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
